import { createContext, useContext } from 'react';
import type { RpLinkCtx } from '@mlm/shared';

/** A kit card resolved server-side for the `packages` section (Phase 2). */
export type RpPackageCard = {
  productId: string;
  name: string;
  price?: string;
  pv?: string;
  imageUrl?: string;
};

/**
 * Everything a replicated-page section needs that it cannot derive from its own
 * config. Supplied by the HOST app, which keeps @mlm/ui free of any API client,
 * origin, or sanitizer dependency — the same arrangement `RichTextEditor` uses
 * for `onUploadImage`.
 *
 * Two hosts exist and must behave identically: the member app (the live public
 * page) and the admin app (the builder's preview). That is the entire reason
 * this renderer lives in a shared package — the pre-builder editor kept a second
 * copy of the renderer for previewing, and it had already drifted (preview drew
 * an <h2> where live drew an <h1>, and a video previewed as a line of text).
 */
export type RpRenderCtx = {
  /** Sponsor ref + campaign slug, for `resolveRpLink`. */
  link: RpLinkCtx;
  /**
   * The page's id. Used to key per-visitor countdown anchors by page identity
   * rather than URL — the default page is reachable both bare and by slug, and a
   * URL-keyed anchor would restart the clock between the two. Absent in preview.
   */
  pageId?: string;
  /** R2 object key → a fetchable image URL. */
  imageUrl: (key: string) => string;
  /** Pre-builder block-row id → its image URL (the legacy per-block route). */
  legacyImageUrl: (blockId: string) => string;
  /**
   * Stored rich-text HTML → HTML safe to inject. The host owns BOTH steps:
   * absolutizing origin-relative inline image srcs, and sanitizing. Sanitizing
   * here is defense-in-depth — the API already sanitizes on write — but the
   * preview must run it too, so that authoring is never a trusted-HTML mode.
   */
  renderHtml: (html: string) => string;
  /**
   * `preview` adds click-to-select affordances. Purely additive: the rendered
   * markup of the page itself is identical in both modes.
   */
  mode?: 'live' | 'preview';
  onSelectSection?: (id: string) => void;
  selectedSectionId?: string | null;
  /** Kit cards for the `packages` section, resolved from the page's tags. */
  packages?: RpPackageCard[];
};

const noop = (s: string) => s;

const FALLBACK: RpRenderCtx = {
  link: {},
  imageUrl: noop,
  legacyImageUrl: noop,
  renderHtml: noop,
  mode: 'live',
};

const Ctx = createContext<RpRenderCtx>(FALLBACK);

export const RpRenderProvider = Ctx.Provider;

export function useRpCtx(): RpRenderCtx {
  return useContext(Ctx);
}
